/**
 * Icon compatibility map: one semantic name → the lucide glyph (design v1, unchanged) and the
 * Phosphor regular glyph (design v2).
 *
 * Migration plan: 387 files import lucide-react directly. Phase 1 routes the shared ui
 * primitives (dialog close, select carets, toasts) and the showcase through <Icon>; the shell
 * and nav move in phase 2; page-level icons migrate screen by screen in phases 3–4. Add a name
 * here when a screen needs one. When v2 ships to everyone, drop the lucide column.
 */
import type { LucideIcon } from 'lucide-react'
import {
  ArrowLeft, ArrowRight, Bell, Bot, Calendar, Camera, Check, ChevronDown, ChevronLeft,
  ChevronRight, ChevronUp, CircleCheck, Coins, Copy, ExternalLink, Flame, Gamepad2, Gift, Heart,
  Home, Image, Info, LayoutGrid, LoaderCircle, Lock, MapPin, Medal, Menu, MessageCircle, Minus,
  Moon, Newspaper, OctagonX, Package, Pencil, Plus, Search, Settings, Share2, ShoppingCart,
  Sparkles, Star, Store, Sun, Ticket, Trash2, TriangleAlert, Trophy, Truck, User, Volume2,
  VolumeX, Wallet, X,
  Activity, AppWindow, BadgeCheck, BarChart3, Briefcase, Car, Clapperboard, ClipboardList, Clock,
  Columns3, Compass, FileText, Film, Flag, FlaskConical, Gauge, Globe, LayoutDashboard, LifeBuoy,
  List as LList, LogOut, Mail, Map as LMap, Megaphone, Navigation, Palette, PlusCircle, Route,
  ShieldAlert, ShieldCheck, ShoppingBag, TrendingUp, Users, Video, Warehouse, Wrench,
  ArrowUp, BellDot, Bookmark, CheckCheck, EyeOff, Feather, ListFilter, MessageSquare, MoreHorizontal,
  Repeat, Send,
  Ban, CreditCard, Lightbulb, MessageSquareWarning, ThumbsUp, Images, Receipt,
  Utensils, QrCode, LocateFixed, Hourglass, Crown, Hash, Rss, Kanban, LogIn, Link as LLink, UserPlus,
  Eye, ArrowUpRight, ThumbsDown, ScanLine, DoorOpen, Phone, TrendingDown,
  Play, Upload, Undo2,
  Tag as LTag,
  RefreshCw, CalendarPlus, CalendarCheck, Inbox, Landmark, Fingerprint,
} from 'lucide-react'
import {
  ArrowLeft as PArrowLeft, ArrowRight as PArrowRight, ArrowSquareOut, Bell as PBell,
  CalendarBlank, Camera as PCamera, CaretDown, CaretLeft, CaretRight, CaretUp, ChatCircle,
  Check as PCheck, CheckCircle, CircleNotch, Coins as PCoins, Copy as PCopy, Fire, GameController,
  Gear, Gift as PGift, Heart as PHeart, House, Image as PImage, Info as PInfo, List, LockSimple,
  MagnifyingGlass, MapPin as PMapPin, Medal as PMedal, Minus as PMinus, Moon as PMoon,
  Newspaper as PNewspaper, Package as PPackage, PencilSimple, Plus as PPlus, Robot, ShareNetwork,
  ShoppingCart as PShoppingCart, Sparkle, SpeakerHigh, SpeakerSlash, SquaresFour, Star as PStar,
  Storefront, Sun as PSun, Ticket as PTicket, Trash, Trophy as PTrophy, Truck as PTruck,
  User as PUser, Wallet as PWallet, Warning, X as PX, XCircle,
  AppWindow as PAppWindow, Briefcase as PBriefcase, Car as PCar, ChartBar, ClipboardText,
  Clock as PClock, Columns, Compass as PCompass, Envelope, FilmSlate, FilmStrip, FileText as PFileText,
  Flag as PFlag, Flask, Gauge as PGauge, Globe as PGlobe, Lifebuoy, ListBullets, MapTrifold,
  Megaphone as PMegaphone, NavigationArrow, Palette as PPalette, Path, PlusCircle as PPlusCircle,
  Pulse, SealCheck, ShieldCheck as PShieldCheck, ShieldWarning, ShoppingBag as PShoppingBag, SignOut,
  TrendUp, Users as PUsers, VideoCamera, Warehouse as PWarehouse, Wrench as PWrench,
  ArrowUp as PArrowUp, BellRinging, BookmarkSimple, ChatTeardropText, Checks, DotsThree, EyeSlash,
  Feather as PFeather, PaperPlaneRight, Repeat as PRepeat, SlidersHorizontal,
  Prohibit, CreditCard as PCreditCard, Lightbulb as PLightbulb, ChatCenteredText, ThumbsUp as PThumbsUp,
  Images as PImages, Receipt as PReceipt,
  ForkKnife, QrCode as PQrCode, Crosshair, Hourglass as PHourglass, Crown as PCrown, Hash as PHash,
  Rss as PRss, Kanban as PKanban, SignIn, LinkSimple, UserPlus as PUserPlus, Eye as PEye,
  ArrowUpRight as PArrowUpRight, ThumbsDown as PThumbsDown, Scan, DoorOpen as PDoorOpen, Phone as PPhone, TrendDown,
  Play as PPlay, UploadSimple, ArrowCounterClockwise,
  Tag as PTag,
  ArrowsClockwise, CalendarPlus as PCalendarPlus, CalendarCheck as PCalendarCheck, Tray, Bank, Fingerprint as PFingerprint,
} from '@phosphor-icons/react'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'

export const ICONS = {
  arrowLeft: { lucide: ArrowLeft, phosphor: PArrowLeft },
  arrowRight: { lucide: ArrowRight, phosphor: PArrowRight },
  bell: { lucide: Bell, phosphor: PBell },
  calendar: { lucide: Calendar, phosphor: CalendarBlank },
  camera: { lucide: Camera, phosphor: PCamera },
  cart: { lucide: ShoppingCart, phosphor: PShoppingCart },
  check: { lucide: Check, phosphor: PCheck },
  chevronDown: { lucide: ChevronDown, phosphor: CaretDown },
  chevronLeft: { lucide: ChevronLeft, phosphor: CaretLeft },
  chevronRight: { lucide: ChevronRight, phosphor: CaretRight },
  chevronUp: { lucide: ChevronUp, phosphor: CaretUp },
  close: { lucide: X, phosphor: PX },
  coins: { lucide: Coins, phosphor: PCoins },
  copy: { lucide: Copy, phosphor: PCopy },
  edit: { lucide: Pencil, phosphor: PencilSimple },
  error: { lucide: OctagonX, phosphor: XCircle },
  external: { lucide: ExternalLink, phosphor: ArrowSquareOut },
  flame: { lucide: Flame, phosphor: Fire },
  game: { lucide: Gamepad2, phosphor: GameController },
  gift: { lucide: Gift, phosphor: PGift },
  grid: { lucide: LayoutGrid, phosphor: SquaresFour },
  heart: { lucide: Heart, phosphor: PHeart },
  home: { lucide: Home, phosphor: House },
  image: { lucide: Image, phosphor: PImage },
  info: { lucide: Info, phosphor: PInfo },
  loading: { lucide: LoaderCircle, phosphor: CircleNotch },
  lock: { lucide: Lock, phosphor: LockSimple },
  mapPin: { lucide: MapPin, phosphor: PMapPin },
  medal: { lucide: Medal, phosphor: PMedal },
  menu: { lucide: Menu, phosphor: List },
  messages: { lucide: MessageCircle, phosphor: ChatCircle },
  minus: { lucide: Minus, phosphor: PMinus },
  moon: { lucide: Moon, phosphor: PMoon },
  muchi: { lucide: Bot, phosphor: Robot },
  news: { lucide: Newspaper, phosphor: PNewspaper },
  package: { lucide: Package, phosphor: PPackage },
  plus: { lucide: Plus, phosphor: PPlus },
  search: { lucide: Search, phosphor: MagnifyingGlass },
  settings: { lucide: Settings, phosphor: Gear },
  share: { lucide: Share2, phosphor: ShareNetwork },
  shop: { lucide: Store, phosphor: Storefront },
  soundOff: { lucide: VolumeX, phosphor: SpeakerSlash },
  soundOn: { lucide: Volume2, phosphor: SpeakerHigh },
  sparkle: { lucide: Sparkles, phosphor: Sparkle },
  star: { lucide: Star, phosphor: PStar },
  success: { lucide: CircleCheck, phosphor: CheckCircle },
  sun: { lucide: Sun, phosphor: PSun },
  ticket: { lucide: Ticket, phosphor: PTicket },
  trash: { lucide: Trash2, phosphor: Trash },
  trophy: { lucide: Trophy, phosphor: PTrophy },
  truck: { lucide: Truck, phosphor: PTruck },
  user: { lucide: User, phosphor: PUser },
  wallet: { lucide: Wallet, phosphor: PWallet },
  warning: { lucide: TriangleAlert, phosphor: Warning },
  // Shell + navigation (phase 2). Registry icon names map onto these in lib/navigation-v2.ts.
  activity: { lucide: Activity, phosphor: Pulse },
  app: { lucide: AppWindow, phosphor: PAppWindow },
  bag: { lucide: ShoppingBag, phosphor: PShoppingBag },
  badgeCheck: { lucide: BadgeCheck, phosphor: SealCheck },
  briefcase: { lucide: Briefcase, phosphor: PBriefcase },
  car: { lucide: Car, phosphor: PCar },
  chart: { lucide: BarChart3, phosphor: ChartBar },
  clapperboard: { lucide: Clapperboard, phosphor: FilmSlate },
  clipboard: { lucide: ClipboardList, phosphor: ClipboardText },
  clock: { lucide: Clock, phosphor: PClock },
  columns: { lucide: Columns3, phosphor: Columns },
  compass: { lucide: Compass, phosphor: PCompass },
  dashboard: { lucide: LayoutDashboard, phosphor: SquaresFour },
  file: { lucide: FileText, phosphor: PFileText },
  film: { lucide: Film, phosphor: FilmStrip },
  flag: { lucide: Flag, phosphor: PFlag },
  flask: { lucide: FlaskConical, phosphor: Flask },
  gauge: { lucide: Gauge, phosphor: PGauge },
  globe: { lucide: Globe, phosphor: PGlobe },
  help: { lucide: LifeBuoy, phosphor: Lifebuoy },
  list: { lucide: LList, phosphor: ListBullets },
  mail: { lucide: Mail, phosphor: Envelope },
  map: { lucide: LMap, phosphor: MapTrifold },
  megaphone: { lucide: Megaphone, phosphor: PMegaphone },
  navigation: { lucide: Navigation, phosphor: NavigationArrow },
  palette: { lucide: Palette, phosphor: PPalette },
  plusCircle: { lucide: PlusCircle, phosphor: PPlusCircle },
  route: { lucide: Route, phosphor: Path },
  shield: { lucide: ShieldCheck, phosphor: PShieldCheck },
  shieldAlert: { lucide: ShieldAlert, phosphor: ShieldWarning },
  signOut: { lucide: LogOut, phosphor: SignOut },
  trendingUp: { lucide: TrendingUp, phosphor: TrendUp },
  users: { lucide: Users, phosphor: PUsers },
  video: { lucide: Video, phosphor: VideoCamera },
  warehouse: { lucide: Warehouse, phosphor: PWarehouse },
  wrench: { lucide: Wrench, phosphor: PWrench },
  // Home + social screens (phase 3.1).
  arrowUp: { lucide: ArrowUp, phosphor: PArrowUp },
  bellDot: { lucide: BellDot, phosphor: BellRinging },
  bookmark: { lucide: Bookmark, phosphor: BookmarkSimple },
  checkAll: { lucide: CheckCheck, phosphor: Checks },
  compose: { lucide: Feather, phosphor: PFeather },
  feeds: { lucide: ListFilter, phosphor: SlidersHorizontal },
  hide: { lucide: EyeOff, phosphor: EyeSlash },
  more: { lucide: MoreHorizontal, phosphor: DotsThree },
  reply: { lucide: MessageSquare, phosphor: ChatTeardropText },
  repost: { lucide: Repeat, phosphor: PRepeat },
  send: { lucide: Send, phosphor: PaperPlaneRight },
  // Shop screens (phase 3.2).
  ban: { lucide: Ban, phosphor: Prohibit },
  card: { lucide: CreditCard, phosphor: PCreditCard },
  dispute: { lucide: MessageSquareWarning, phosphor: ChatCenteredText },
  idea: { lucide: Lightbulb, phosphor: PLightbulb },
  images: { lucide: Images, phosphor: PImages },
  receipt: { lucide: Receipt, phosphor: PReceipt },
  thumbsUp: { lucide: ThumbsUp, phosphor: PThumbsUp },
  // Discover and community (phase 4A.1).
  utensils: { lucide: Utensils, phosphor: ForkKnife },
  qrCode: { lucide: QrCode, phosphor: PQrCode },
  locate: { lucide: LocateFixed, phosphor: Crosshair },
  hourglass: { lucide: Hourglass, phosphor: PHourglass },
  crown: { lucide: Crown, phosphor: PCrown },
  hash: { lucide: Hash, phosphor: PHash },
  rss: { lucide: Rss, phosphor: PRss },
  kanban: { lucide: Kanban, phosphor: PKanban },
  signIn: { lucide: LogIn, phosphor: SignIn },
  link: { lucide: LLink, phosphor: LinkSimple },
  userPlus: { lucide: UserPlus, phosphor: PUserPlus },
  eye: { lucide: Eye, phosphor: PEye },
  arrowUpRight: { lucide: ArrowUpRight, phosphor: PArrowUpRight },
  thumbsDown: { lucide: ThumbsDown, phosphor: PThumbsDown },
  scan: { lucide: ScanLine, phosphor: Scan },
  door: { lucide: DoorOpen, phosphor: PDoorOpen },
  phone: { lucide: Phone, phosphor: PPhone },
  trendingDown: { lucide: TrendingDown, phosphor: TrendDown },
  play: { lucide: Play, phosphor: PPlay },
  upload: { lucide: Upload, phosphor: UploadSimple },
  undo: { lucide: Undo2, phosphor: ArrowCounterClockwise },
  tag: { lucide: LTag, phosphor: PTag },
  refresh: { lucide: RefreshCw, phosphor: ArrowsClockwise },
  calendarPlus: { lucide: CalendarPlus, phosphor: PCalendarPlus },
  calendarCheck: { lucide: CalendarCheck, phosphor: PCalendarCheck },
  inbox: { lucide: Inbox, phosphor: Tray },
  bank: { lucide: Landmark, phosphor: Bank },
  fingerprint: { lucide: Fingerprint, phosphor: PFingerprint },
} satisfies Record<string, { lucide: LucideIcon; phosphor: PhosphorIcon }>

export type IconName = keyof typeof ICONS
export const ICON_NAMES = Object.keys(ICONS).sort() as IconName[]
